export type FontFaceId =
  | "arial"
  | "verdana"
  | "tahoma"
  | "georgia"
  | "times"
  | "courier";

export type FontSizeId = "10" | "12" | "14" | "16" | "18" | "20" | "24";

export type Align = "left" | "center" | "right";

export type RichToolbarState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  align: Align;
  font: FontFaceId;
  size: FontSizeId;
  unorderedList: boolean;
  orderedList: boolean;
};

export const DEFAULT_RICH_TOOLBAR_STATE: RichToolbarState = {
  bold: false,
  italic: false,
  underline: false,
  align: "left",
  font: "arial",
  size: "14",
  unorderedList: false,
  orderedList: false,
};

export const FONT_FACES: ReadonlyArray<{
  id: FontFaceId;
  label: string;
  stack: string;
  command: string;
}> = [
  { id: "arial", label: "Arial", stack: "Arial, Helvetica, sans-serif", command: "Arial" },
  { id: "verdana", label: "Verdana", stack: "Verdana, Geneva, sans-serif", command: "Verdana" },
  { id: "tahoma", label: "Tahoma", stack: "Tahoma, Geneva, sans-serif", command: "Tahoma" },
  { id: "georgia", label: "Georgia", stack: "Georgia, serif", command: "Georgia" },
  {
    id: "times",
    label: "Times New Roman",
    stack: '"Times New Roman", Times, serif',
    command: "Times New Roman",
  },
  {
    id: "courier",
    label: "Courier New",
    stack: '"Courier New", Courier, monospace',
    command: "Courier New",
  },
];

export const FONT_SIZES: ReadonlyArray<{ id: FontSizeId; px: number; exec: string }> = [
  { id: "10", px: 10, exec: "1" },
  { id: "12", px: 12, exec: "2" },
  { id: "14", px: 14, exec: "3" },
  { id: "16", px: 16, exec: "4" },
  { id: "18", px: 18, exec: "5" },
  { id: "20", px: 20, exec: "6" },
  { id: "24", px: 24, exec: "7" },
];

const EXEC_SIZE_TO_PX = new Map(FONT_SIZES.map((entry) => [entry.exec, entry.px]));

function safeQueryState(command: string) {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

function nearestSizeId(px: number): FontSizeId {
  let best = FONT_SIZES[2];
  let bestDelta = Math.abs(px - best.px);
  for (const entry of FONT_SIZES) {
    const delta = Math.abs(px - entry.px);
    if (delta < bestDelta) {
      best = entry;
      bestDelta = delta;
    }
  }
  return best.id;
}

export function matchFontId(familyRaw: string): FontFaceId {
  const family = familyRaw.toLowerCase();
  if (family.includes("courier")) return "courier";
  if (family.includes("georgia")) return "georgia";
  if (family.includes("times")) return "times";
  if (family.includes("tahoma")) return "tahoma";
  if (family.includes("verdana")) return "verdana";
  return "arial";
}

function selectionInList(editor: HTMLElement, tagName: "UL" | "OL"): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  let node: Node | null = sel.anchorNode;
  while (node && node !== editor) {
    if (node instanceof HTMLElement && node.tagName === tagName) return true;
    node = node.parentNode;
  }
  return false;
}

function blockParent(editor: HTMLElement, node: Node | null): HTMLElement | null {
  let el = node instanceof HTMLElement ? node : node?.parentElement ?? null;
  while (el && el !== editor) {
    const tag = el.tagName;
    if (tag === "P" || tag === "DIV" || tag === "LI" || tag === "TD" || tag === "TH") return el;
    el = el.parentElement;
  }
  return editor;
}

function styleParent(editor: HTMLElement, node: Node | null): HTMLElement | null {
  let el = node instanceof HTMLElement ? node : node?.parentElement ?? null;
  while (el && el !== editor) {
    if (el.tagName === "SPAN" || el.tagName === "P" || el.tagName === "DIV" || el.tagName === "LI") {
      return el;
    }
    el = el.parentElement;
  }
  return node instanceof HTMLElement ? node : node?.parentElement ?? editor;
}

function hasBoldAncestor(node: Node | null, editor: HTMLElement): boolean {
  let el = node instanceof HTMLElement ? node : node?.parentElement ?? null;
  while (el && el !== editor) {
    const tag = el.tagName;
    if (tag === "B" || tag === "STRONG") return true;
    const weight = window.getComputedStyle(el).fontWeight;
    if (weight === "bold" || Number.parseInt(weight, 10) >= 600) return true;
    el = el.parentElement;
  }
  return false;
}

function hasItalicAncestor(node: Node | null, editor: HTMLElement): boolean {
  let el = node instanceof HTMLElement ? node : node?.parentElement ?? null;
  while (el && el !== editor) {
    const tag = el.tagName;
    if (tag === "I" || tag === "EM") return true;
    if (window.getComputedStyle(el).fontStyle === "italic") return true;
    el = el.parentElement;
  }
  return false;
}

export function readSelectionToolbarState(editor: HTMLElement | null): RichToolbarState {
  if (!editor) return DEFAULT_RICH_TOOLBAR_STATE;

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !editor.contains(sel.anchorNode)) {
    return DEFAULT_RICH_TOOLBAR_STATE;
  }

  const styleEl = styleParent(editor, sel.anchorNode);
  const blockEl = blockParent(editor, sel.anchorNode);
  const computed = styleEl ? window.getComputedStyle(styleEl) : null;
  const blockComputed = blockEl ? window.getComputedStyle(blockEl) : null;

  const bold = safeQueryState("bold") || hasBoldAncestor(sel.anchorNode, editor);
  const italic = safeQueryState("italic") || hasItalicAncestor(sel.anchorNode, editor);
  const underline =
    safeQueryState("underline") ||
    Boolean(computed?.textDecorationLine.includes("underline"));

  let align: Align = "left";
  const textAlign = blockComputed?.textAlign ?? "left";
  if (textAlign === "center") align = "center";
  else if (textAlign === "right") align = "right";

  const font = matchFontId(computed?.fontFamily ?? "Arial, sans-serif");
  const px = computed ? Number.parseFloat(computed.fontSize) : 14;
  const size = nearestSizeId(Number.isFinite(px) ? px : 14);

  return {
    bold,
    italic,
    underline,
    align,
    font,
    size,
    unorderedList: selectionInList(editor, "UL") || safeQueryState("insertUnorderedList"),
    orderedList: selectionInList(editor, "OL") || safeQueryState("insertOrderedList"),
  };
}

function parsePxFromFontSize(value: string): number | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed.endsWith("px")) {
    const px = Number.parseFloat(trimmed);
    return Number.isFinite(px) ? px : null;
  }
  const map: Record<string, number> = {
    "xx-small": 10,
    "x-small": 11,
    small: 13,
    medium: 16,
    large: 18,
    "x-large": 24,
    "xx-large": 32,
  };
  return map[trimmed] ?? null;
}

function mergeSpanStyle(existing: string | null, addition: string): string {
  const base = (existing ?? "").trim().replace(/;+\s*$/, "");
  if (!base) return addition;
  const key = addition.split(":")[0]?.trim().toLowerCase();
  if (!key) return base;
  const parts = base
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !part.toLowerCase().startsWith(`${key}:`));
  parts.push(addition);
  return parts.join("; ");
}

/** Normalise le HTML émis (font legacy, tailles relatives → px). */
export function normalizeRichHtml(html: string): string {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");

  for (const font of [...doc.querySelectorAll("font")]) {
    const span = doc.createElement("span");
    const styles: string[] = [];
    const face = font.getAttribute("face")?.trim();
    const size = font.getAttribute("size")?.trim();
    if (face) styles.push(`font-family: ${face}`);
    const px = size ? EXEC_SIZE_TO_PX.get(size) : undefined;
    if (px) styles.push(`font-size: ${px}px`);
    if (styles.length) span.setAttribute("style", styles.join("; "));
    span.innerHTML = font.innerHTML;
    font.replaceWith(span);
  }

  for (const span of doc.querySelectorAll("span[style]")) {
    const el = span as HTMLElement;
    const style = el.getAttribute("style") ?? "";
    const sizeMatch = style.match(/font-size\s*:\s*([^;]+)/i);
    if (!sizeMatch) continue;
    const px = parsePxFromFontSize(sizeMatch[1]);
    if (!px) continue;
    const fixedPx = FONT_SIZES.find((entry) => entry.px === px)?.px ?? px;
    el.setAttribute("style", mergeSpanStyle(style, `font-size: ${fixedPx}px`));
  }

  // Retours à la ligne propres pour l’envoi (évite des div vides empilés).
  for (const div of [...doc.querySelectorAll("div")]) {
    if (div.innerHTML.trim() === "" || div.innerHTML.trim() === "<br>") {
      div.replaceWith(doc.createElement("br"));
    }
  }

  return doc.body.innerHTML;
}

function cssProperty(key: string): string {
  return key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

export type SelectionStore = {
  current: Range | null;
  startPath: number[];
  startOffset: number;
  endPath: number[];
  endOffset: number;
};

export const EMPTY_SELECTION: SelectionStore = {
  current: null,
  startPath: [],
  startOffset: 0,
  endPath: [],
  endOffset: 0,
};

function nodePathFromEditor(editor: HTMLElement, target: Node): number[] {
  const path: number[] = [];
  let node: Node | null = target;
  while (node && node !== editor) {
    const parent: Node | null = node.parentNode;
    if (!parent) return [];
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, node));
    node = parent;
  }
  return node === editor ? path : [];
}

function resolveNodePath(editor: HTMLElement, path: number[]): Node | null {
  let node: Node = editor;
  for (const index of path) {
    if (index < 0 || index >= node.childNodes.length) return null;
    node = node.childNodes[index]!;
  }
  return node;
}

function safeRangeOffset(node: Node, offset: number): number {
  if (node.nodeType === Node.TEXT_NODE) {
    return Math.max(0, Math.min(offset, node.textContent?.length ?? 0));
  }
  return Math.max(0, Math.min(offset, node.childNodes.length));
}

function applyInlineStyle(
  editor: HTMLElement | null,
  saved: SelectionStore,
  styles: Record<string, string>
) {
  focusEditorAndRestoreSelection(editor, saved);

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !editor) return;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return;

  document.execCommand("styleWithCSS", false, "true");

  const span = document.createElement("span");
  for (const [key, value] of Object.entries(styles)) {
    span.style.setProperty(cssProperty(key), value);
  }

  if (range.collapsed) {
    span.appendChild(document.createTextNode("\u200B"));
    range.insertNode(span);
    const cursor = document.createRange();
    cursor.setStart(span.firstChild!, 1);
    cursor.collapse(true);
    sel.removeAllRanges();
    sel.addRange(cursor);
    saveEditorSelection(editor, saved);
    return;
  }

  try {
    const extracted = range.extractContents();
    span.appendChild(extracted);
    range.insertNode(span);
    const after = document.createRange();
    after.selectNodeContents(span);
    after.collapse(false);
    sel.removeAllRanges();
    sel.addRange(after);
    saveEditorSelection(editor, saved);
  } catch {
    const styleAttr = Object.entries(styles)
      .map(([key, value]) => `${cssProperty(key)}:${value}`)
      .join(";");
    document.execCommand(
      "insertHTML",
      false,
      `<span style="${styleAttr}">${range.toString()}</span>`
    );
  }
}

export function cloneSelectionStore(saved: SelectionStore): SelectionStore {
  return {
    current: saved.current?.cloneRange() ?? null,
    startPath: [...saved.startPath],
    startOffset: saved.startOffset,
    endPath: [...saved.endPath],
    endOffset: saved.endOffset,
  };
}

export function restoreEditorSelectionFromPaths(
  editor: HTMLElement | null,
  saved: SelectionStore
): boolean {
  const sel = window.getSelection();
  if (!sel || !editor || saved.startPath.length === 0) return false;

  const startNode = resolveNodePath(editor, saved.startPath);
  const endNode = resolveNodePath(editor, saved.endPath);
  if (!startNode || !endNode) return false;

  const next = document.createRange();
  next.setStart(startNode, safeRangeOffset(startNode, saved.startOffset));
  next.setEnd(endNode, safeRangeOffset(endNode, saved.endOffset));
  sel.removeAllRanges();
  sel.addRange(next);
  saved.current = next.cloneRange();
  return true;
}

function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Insère un lien à l’emplacement sauvegardé (sans execCommand createLink). */
export function applyEditorLink(
  editor: HTMLElement | null,
  saved: SelectionStore,
  url: string
): boolean {
  if (!editor || saved.startPath.length === 0) return false;

  editor.focus({ preventScroll: true });
  if (!restoreEditorSelectionFromPaths(editor, saved)) return false;

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return false;

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener noreferrer";
  anchor.target = "_blank";

  if (range.collapsed) {
    anchor.textContent = url;
    range.insertNode(anchor);
    const after = document.createRange();
    after.setStartAfter(anchor);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    saveEditorSelection(editor, saved);
    return true;
  }

  try {
    const contents = range.extractContents();
    anchor.appendChild(contents);
    range.insertNode(anchor);
    const after = document.createRange();
    after.setStartAfter(anchor);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    saveEditorSelection(editor, saved);
    return true;
  } catch {
    const text = range.toString();
    const safeUrl = escapeHtmlText(url);
    const safeText = escapeHtmlText(text) || safeUrl;
    document.execCommand(
      "insertHTML",
      false,
      `<a href="${safeUrl}" rel="noopener noreferrer" target="_blank">${safeText}</a>`
    );
    saveEditorSelection(editor, saved);
    return true;
  }
}

export function saveEditorSelection(editor: HTMLElement | null, saved: SelectionStore) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !editor) return;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return;
  saved.current = range.cloneRange();
  saved.startPath = nodePathFromEditor(editor, range.startContainer);
  saved.startOffset = range.startOffset;
  saved.endPath = nodePathFromEditor(editor, range.endContainer);
  saved.endOffset = range.endOffset;
}

export function restoreEditorSelection(editor: HTMLElement | null, saved: SelectionStore) {
  const sel = window.getSelection();
  if (!sel) return;

  const range = saved.current;
  if (range?.startContainer.isConnected && range.endContainer.isConnected) {
    try {
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    } catch {
      /* fallback path ci-dessous */
    }
  }

  if (!editor || saved.startPath.length === 0) return;
  const startNode = resolveNodePath(editor, saved.startPath);
  const endNode = resolveNodePath(editor, saved.endPath);
  if (!startNode || !endNode) return;

  const next = document.createRange();
  next.setStart(startNode, safeRangeOffset(startNode, saved.startOffset));
  next.setEnd(endNode, safeRangeOffset(endNode, saved.endOffset));
  sel.removeAllRanges();
  sel.addRange(next);
  saved.current = next.cloneRange();
}

export function focusEditorAndRestoreSelection(
  editor: HTMLElement | null,
  saved: SelectionStore
) {
  editor?.focus({ preventScroll: true });
  restoreEditorSelection(editor, saved);
}

export function execRichCommand(
  editor: HTMLElement | null,
  saved: SelectionStore,
  command: string,
  value?: string
) {
  focusEditorAndRestoreSelection(editor, saved);
  document.execCommand("styleWithCSS", false, "true");
  document.execCommand(command, false, value);
  saveEditorSelection(editor, saved);
}

export function applyFontFamily(
  editor: HTMLElement | null,
  saved: SelectionStore,
  face: (typeof FONT_FACES)[number]
) {
  focusEditorAndRestoreSelection(editor, saved);
  document.execCommand("styleWithCSS", false, "true");
  document.execCommand("fontName", false, face.command);
  saveEditorSelection(editor, saved);
}

export function applyFontSize(
  editor: HTMLElement | null,
  saved: SelectionStore,
  size: (typeof FONT_SIZES)[number]
) {
  applyInlineStyle(editor, saved, { fontSize: `${size.px}px` });
}

export function initRichEditorParagraphMode(editor: HTMLElement) {
  const previous = document.activeElement;
  editor.focus({ preventScroll: true });
  document.execCommand("defaultParagraphSeparator", false, "p");
  document.execCommand("styleWithCSS", false, "true");
  if (previous instanceof HTMLElement && previous !== editor) {
    previous.focus({ preventScroll: true });
  }
}
